import React from 'react';
import { bool, string } from 'prop-types';
import classNames from 'classnames';

import { intlShape } from '../../util/reactIntl';
import * as validators from '../../util/validators';
import getCountryCodes from '../../translations/countryCodes';
import { postcodeValidator, shippingCountries } from '../../config/configShipping';
import FieldSelect from '../FieldSelect/FieldSelect';
import FieldTextInput from '../FieldTextInput/FieldTextInput';

import css from './ShipFromAddressFields.module.css';

/**
 * The seller's posting address — where their parcels are collected from or handed
 * in — as a group of form fields.
 *
 * Asked for in two places, which is why it is a component rather than part of
 * either form:
 *
 * - the listing delivery step, so a shipping listing cannot go live without one
 *   (P25028-120). Postage is quoted from this address at checkout, so an order
 *   cannot be priced until it exists.
 * - the label form on the order page (P25028-92), for sellers whose listings were
 *   made before that rule.
 *
 * It renders fields only. The surrounding form owns the heading, the submit button
 * and what happens to the values — see `src/util/shipFromAddress.js` for turning
 * them into the address that gets stored.
 *
 * `required` exists because the listing form shows these fields while shipping is
 * switched off, and a hidden-but-required field is a form that cannot be submitted
 * with no visible reason why.
 */
const ShipFromAddressFields = props => {
  const { className, disabled, fieldClassName, formId, intl, locale, required, rowClassName } =
    props;

  const fieldId = name => (formId ? `${formId}.${name}` : name);
  const fieldClasses = classNames(fieldClassName || css.field);

  // Validation has to be added and removed as a whole, not merely ignored, or
  // final-form keeps the last error it saw and the submit button stays dead.
  const requiredMessage = id =>
    required ? validators.required(intl.formatMessage({ id })) : undefined;

  // Only the countries Causee posts from. See configShipping for why, and note
  // the server checks this too — the endpoints are reachable without this form.
  const countryCodes = shippingCountries(getCountryCodes(locale));
  const optionalText = intl.formatMessage({ id: 'ShipFromAddress.optionalText' });

  return (
    <div className={classNames(className)}>
      <FieldTextInput
        className={fieldClasses}
        id={fieldId('shipFromName')}
        name="shipFromName"
        type="text"
        autoComplete="name"
        disabled={disabled}
        label={intl.formatMessage({ id: 'ShipFromAddress.nameLabel' })}
        validate={requiredMessage('ShipFromAddress.nameRequired')}
        // The validator has to be re-registered with final-form when it changes,
        // and only a new key does that.
        // See https://codesandbox.io/s/changing-field-level-validators-zc8ei
        key={required ? 'shipFromNameRequired' : 'shipFromNameOptional'}
      />
      <FieldTextInput
        className={fieldClasses}
        id={fieldId('shipFromPhoneNumber')}
        name="shipFromPhoneNumber"
        type="text"
        autoComplete="tel"
        disabled={disabled}
        label={intl.formatMessage({ id: 'ShipFromAddress.phoneLabel' }, { optionalText })}
      />
      <FieldTextInput
        className={fieldClasses}
        id={fieldId('shipFromLine1')}
        name="shipFromLine1"
        type="text"
        autoComplete="address-line1"
        disabled={disabled}
        label={intl.formatMessage({ id: 'ShipFromAddress.line1Label' })}
        validate={requiredMessage('ShipFromAddress.line1Required')}
        key={required ? 'shipFromLine1Required' : 'shipFromLine1Optional'}
      />
      <FieldTextInput
        className={fieldClasses}
        id={fieldId('shipFromLine2')}
        name="shipFromLine2"
        type="text"
        autoComplete="address-line2"
        disabled={disabled}
        label={intl.formatMessage({ id: 'ShipFromAddress.line2Label' }, { optionalText })}
      />
      <div className={classNames(rowClassName || css.fieldRow)}>
        <FieldTextInput
          className={fieldClasses}
          id={fieldId('shipFromPostalCode')}
          name="shipFromPostalCode"
          type="text"
          autoComplete="postal-code"
          disabled={disabled}
          label={intl.formatMessage({ id: 'ShipFromAddress.postalCodeLabel' })}
          // Required, and a postcode of the chosen country: every quote is taken
          // from this address, so a wrong one misprices every order. The server
          // checks this again.
          validate={
            required
              ? postcodeValidator(
                  {
                    required: intl.formatMessage({ id: 'ShipFromAddress.postalCodeRequired' }),
                    invalid: intl.formatMessage({ id: 'ShipFromAddress.postalCodeInvalid' }),
                    outsideUk: intl.formatMessage({ id: 'ShipFromAddress.postalCodeOutsideUk' }),
                  },
                  'shipFromCountry'
                )
              : undefined
          }
          key={required ? 'shipFromPostalCodeRequired' : 'shipFromPostalCodeOptional'}
        />
        <FieldTextInput
          className={fieldClasses}
          id={fieldId('shipFromCity')}
          name="shipFromCity"
          type="text"
          autoComplete="address-level2"
          disabled={disabled}
          label={intl.formatMessage({ id: 'ShipFromAddress.cityLabel' })}
          validate={requiredMessage('ShipFromAddress.cityRequired')}
          key={required ? 'shipFromCityRequired' : 'shipFromCityOptional'}
        />
      </div>
      <FieldTextInput
        className={fieldClasses}
        id={fieldId('shipFromState')}
        name="shipFromState"
        type="text"
        autoComplete="address-level1"
        disabled={disabled}
        label={intl.formatMessage({ id: 'ShipFromAddress.stateLabel' }, { optionalText })}
      />
      <FieldSelect
        className={fieldClasses}
        id={fieldId('shipFromCountry')}
        name="shipFromCountry"
        autoComplete="country"
        disabled={disabled}
        label={intl.formatMessage({ id: 'ShipFromAddress.countryLabel' })}
        validate={requiredMessage('ShipFromAddress.countryRequired')}
        key={required ? 'shipFromCountryRequired' : 'shipFromCountryOptional'}
      >
        <option disabled value="">
          {intl.formatMessage({ id: 'ShipFromAddress.countryPlaceholder' })}
        </option>
        {countryCodes.map(country => (
          <option key={country.code} value={country.code}>
            {country.name}
          </option>
        ))}
      </FieldSelect>
    </div>
  );
};

ShipFromAddressFields.defaultProps = {
  className: null,
  disabled: false,
  fieldClassName: null,
  formId: null,
  locale: 'en',
  required: true,
  rowClassName: null,
};

ShipFromAddressFields.propTypes = {
  className: string,
  disabled: bool,
  // Lets the host form keep its own spacing for these fields.
  fieldClassName: string,
  formId: string,
  locale: string,
  required: bool,
  rowClassName: string,

  intl: intlShape.isRequired,
};

export default ShipFromAddressFields;
